import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can, type AdminOrgDetail, type OrgInput } from '../lib/adminApi'
import { useCommonCodes, useDongs } from '../lib/codes'

/**
 * 기업 등록·수정.
 *
 * **입력 항목은 `docs/10_설계/입력항목-정의표.md` §1이 정본이다** — 표에 없는 항목을
 * 화면에서 새로 만들지 않는다. 필수 6개(기업명·유형·거점 행정동·운영상태·담당자명·연락처)도
 * 거기서 왔다.
 *
 * **담당자 연락처는 권한이 있을 때만 그린다.** 서버가 `pii.read` 없는 계정에는 값을 아예
 * 안 준다(admin-card.ts). 빈 입력란을 보여 주면 저장할 때 실제 연락처를 빈 값으로
 * 덮어쓰게 되므로, 없으면 입력란 자체를 두지 않고 안내만 남긴다.
 *
 * **저장 ≠ 공개다.** 새 기업은 항상 비공개로 만들어지고, 공개 전환은 검수 권한이 한다.
 */

/** 빈 입력은 `null`로 보낸다 — 빈 문자열을 저장하면 "없음"과 "빈 값"이 섞인다. */
function orNull(v: string): string | null {
  const t = v.trim()
  return t === '' ? null : t
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminOrgEditPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === undefined
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useAdminMe()

  const orgTypes = useCommonCodes('org_type')
  const certTypes = useCommonCodes('cert_type')
  const operationStatuses = useCommonCodes('operation_status')
  const dongs = useDongs()

  const org = useQuery({
    queryKey: ['admin-org', id],
    queryFn: () => adminApi.org(id ?? ''),
    enabled: !isNew,
  })

  const [name, setName] = useState('')
  const [orgType, setOrgType] = useState('')
  const [certType, setCertType] = useState('')
  const [foundedYear, setFoundedYear] = useState('')
  const [memberScale, setMemberScale] = useState('')
  const [intro, setIntro] = useState('')
  // 미션·비전은 구글폼 문항(08-11 회의로 추가)을 담을 자리다 — 018로 컬럼을 만들었다.
  const [mission, setMission] = useState('')
  const [vision, setVision] = useState('')
  const [homepageUrl, setHomepageUrl] = useState('')
  const [baseRegionCode, setBaseRegionCode] = useState('')
  // 소재지 주소 원문(020) — 행정동을 고른 근거다. 공개되지 않아 연락처와 같은 권한 축에 있다.
  const [baseAddress, setBaseAddress] = useState('')
  const [operationStatus, setOperationStatus] = useState('active')
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  // 사업자등록번호 — 원문이 저장되므로(016) pii.read 계정은 저장된 번호가 채워진 채 시작한다.
  // dirty는 **번호 입력란만** 본다: 성명·개업일자만 만진 저장이 "번호를 비웠다 = 지워 달라(null)"로
  // 번지면 기존 확인(valid) 이력이 통째로 지워진다(리뷰 08-06 HIGH). 두 필드는 번호 없이는
  // 서버에서 아무 의미가 없으므로 번호가 안 갈 때는 함께 보내지 않는다.
  const [bizRegNo, setBizRegNo] = useState('')
  // 번호 종류(021) — 사업자등록번호와 고유번호는 형식이 같아 사람이 고른다.
  // 종류를 바꾸면 검증 경로가 바뀌므로 dirty를 세워 번호와 함께 재전송한다.
  const [bizRegKind, setBizRegKind] = useState<'biz_reg' | 'unique_no'>('biz_reg')
  const [ceoName, setCeoName] = useState('')
  const [openDate, setOpenDate] = useState('')
  const [bizRegNoDirty, setBizRegNoDirty] = useState(false)

  // 저장하지 않은 편집 내용을 서버 응답으로 덮어쓰지 않으려고, 폼 초기화는 한 번만 한다.
  const loadedId = useRef<string | null>(null)

  useEffect(() => {
    const o: AdminOrgDetail | undefined = org.data
    if (o === undefined || loadedId.current === o.id) return
    loadedId.current = o.id
    setName(o.name)
    setOrgType(o.orgType)
    setCertType(o.certType ?? '')
    setFoundedYear(o.foundedYear === null ? '' : String(o.foundedYear))
    setMemberScale(o.memberScale ?? '')
    setIntro(o.intro ?? '')
    setMission(o.mission ?? '')
    setVision(o.vision ?? '')
    setHomepageUrl(o.homepageUrl ?? '')
    setBaseRegionCode(o.baseRegionCode)
    setBaseAddress(o.baseAddress ?? '')
    setOperationStatus(o.operationStatus)
    setContactName(o.contactName ?? '')
    setContactPhone(o.contactPhone ?? '')
    setContactEmail(o.contactEmail ?? '')
    // 권한이 없으면 키 자체가 없어 빈 칸이 된다 — dirty 게이트 덕에 안 만지면 서버로 안 간다.
    setBizRegNo(o.bizRegNo ?? '')
    setBizRegKind(o.bizRegKind ?? 'biz_reg')
  }, [org.data])

  // 기업 유형 기본값 — 안 고른 채 저장하면 400이 나므로 목록 첫 항목으로 채운다.
  useEffect(() => {
    if (orgType === '' && orgTypes.data !== undefined && orgTypes.data.length > 0) {
      setOrgType(orgTypes.data[0]?.code ?? '')
    }
  }, [orgTypes.data, orgType])

  // 새 기업은 아직 서버 응답이 없다 — 내 권한으로 판단한다. 수정은 서버가 준 값을 믿는다.
  const mayReadContact = isNew ? can(me.data, 'pii', 'read') : (org.data?.canReadContact ?? false)
  const mayWrite = can(me.data, 'org', 'write')

  const save = useMutation({
    mutationFn: async () => {
      const base = {
        name: name.trim(),
        orgType,
        certType: orNull(certType),
        foundedYear: foundedYear.trim() === '' ? null : Number(foundedYear),
        memberScale: orNull(memberScale),
        intro: orNull(intro),
        mission: orNull(mission),
        vision: orNull(vision),
        homepageUrl: orNull(homepageUrl),
        logoUrl: null,
        baseRegionCode,
        operationStatus,
      }
      // 연락처를 볼 수 없는 계정은 그 필드를 보내지 않는다 — 안 보내면 서버가 손대지 않는다.
      // 소재지 주소도 같은 축이다(020): 못 보는 계정이 빈 칸을 저장해 원본을 지우면 안 된다.
      const contact = mayReadContact
        ? {
            contactName: contactName.trim(),
            contactPhone: contactPhone.trim(),
            contactEmail: orNull(contactEmail),
            baseAddress: orNull(baseAddress),
          }
        : {}
      // 사업자등록번호는 **번호 입력란을 건드렸을 때만** 보낸다. 빈 값 전송(null)은 "지워 달라"다.
      // 대표자 성명·개업일자는 진위확인 입력 전용이라 번호와 함께 갈 때만 의미가 있다.
      // 고유번호는 국세청 확인이 없으므로 두 항목을 보내지 않는다(021).
      const bizReg =
        (isNew && bizRegNo.trim() !== '') || (!isNew && bizRegNoDirty)
          ? {
              bizRegNo: orNull(bizRegNo),
              bizRegKind,
              ceoName: bizRegKind === 'biz_reg' ? orNull(ceoName) : null,
              openDate: bizRegKind === 'biz_reg' ? orNull(openDate) : null,
            }
          : {}
      const input = { ...base, ...contact, ...bizReg } as OrgInput
      if (isNew) return adminApi.createOrg(input)
      return adminApi.updateOrg(id, input)
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-org', res.id] })
      if (isNew) void navigate(`/admin/orgs/${res.id}`, { replace: true })
    },
  })

  const contactFilled = !mayReadContact || (contactName.trim() !== '' && contactPhone.trim() !== '')
  // 사업자등록번호를 넣었으면 진위확인 짝(대표자 성명·개업일자)도 있어야 한다 — 서버도 같은
  // 검사를 한다. 고유번호는 국세청 확인이 없어 두 항목이 필요 없다(021).
  const bizRegFilled =
    bizRegNo.trim() === '' ||
    bizRegKind === 'unique_no' ||
    (ceoName.trim() !== '' && openDate.trim() !== '')
  const canSave =
    mayWrite &&
    name.trim() !== '' &&
    orgType !== '' &&
    baseRegionCode !== '' &&
    // 새 기업은 담당자 연락처가 필수다(정의표 §1). 연락처를 볼 수 없는 계정은 만들 수 없다.
    (!isNew || mayReadContact) &&
    contactFilled &&
    bizRegFilled

  const actionError = errorMessage(save.error)

  return (
    <div>
      <Link className="back-link" to="/admin/orgs">
        ← 기업 관리
      </Link>

      <div className="admin-bar">
        <div>
          <h1 className="page-title">{isNew ? '새 기업' : '기업 수정'}</h1>
          <p className="page-desc">
            {isNew
              ? '저장하면 비공개 상태로 만들어집니다. 해결카드는 저장한 뒤에 붙입니다.'
              : '수정 내용은 저장을 눌러야 반영됩니다.'}
          </p>
        </div>
        {!isNew && org.data !== undefined && (
          <div className="admin-who">
            <span className="muted small">{org.data.isPublished ? '공개 중' : '비공개'}</span>
          </div>
        )}
      </div>

      {isNew && !mayReadContact && (
        <div className="notice error" role="alert">
          담당자 연락처를 다룰 권한(<b>pii.read</b>)이 없어 새 기업을 만들 수 없습니다. 담당자
          연락처는 필수 항목입니다.
        </div>
      )}

      {actionError !== null && (
        <div className="notice error" role="alert">
          {actionError}
        </div>
      )}

      {save.isSuccess && !isNew && (
        <div className="notice" role="status">
          저장했습니다.
        </div>
      )}

      <QueryState isPending={!isNew && org.isPending} error={org.error}>
        <form
          className="panel"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSave) save.mutate()
          }}
        >
          <div className="field">
            <label htmlFor="org-name">
              기업명 <span className="req">*</span>
            </label>
            <input
              id="org-name"
              type="text"
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="org-type">
              기업 유형 <span className="req">*</span>
            </label>
            <select id="org-type" value={orgType} onChange={(e) => setOrgType(e.target.value)}>
              {orgTypes.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="org-cert">인증 구분</label>
            <select id="org-cert" value={certType} onChange={(e) => setCertType(e.target.value)}>
              <option value="">선택 안 함</option>
              {certTypes.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="org-region">
              거점 행정동 <span className="req">*</span>
            </label>
            <select
              id="org-region"
              value={baseRegionCode}
              onChange={(e) => setBaseRegionCode(e.target.value)}
            >
              <option value="">선택해 주세요</option>
              {dongs.data?.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.name}
                </option>
              ))}
            </select>
            <div className="hint">공개 화면과 지도에는 이 동까지만 나갑니다(설계서 MAP-05).</div>
          </div>

          {/*
            소재지 주소는 행정동을 고른 근거다(020). 신청 구글폼이 주소를 자유 입력으로 받는데
            주소만으로는 행정동이 갈리지 않아(법정동코드 문제) 사람이 위에서 고른다 — 그 판단의
            원본을 같은 화면에 둔다. 공개되지 않으므로 연락처와 같은 권한 축에 있다.
          */}
          {mayReadContact ? (
            <div className="field">
              <label htmlFor="org-address">소재지 주소</label>
              <input
                id="org-address"
                value={baseAddress}
                maxLength={300}
                onChange={(e) => setBaseAddress(e.target.value)}
              />
              <div className="hint">
                신청서에 적힌 주소 원문입니다. 관리자만 보이며 공개 화면에는 나가지 않습니다.
              </div>
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="org-operation">
              운영 상태 <span className="req">*</span>
            </label>
            <select
              id="org-operation"
              value={operationStatus}
              onChange={(e) => setOperationStatus(e.target.value)}
            >
              {operationStatuses.data?.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <div className="hint">종료로 바꾸면 공개 목록에서 빠집니다.</div>
          </div>

          <div className="field">
            <label htmlFor="org-founded">설립연도</label>
            <input
              id="org-founded"
              type="number"
              min={1900}
              max={2100}
              value={foundedYear}
              onChange={(e) => setFoundedYear(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="org-scale">인력 규모</label>
            <input
              id="org-scale"
              type="text"
              value={memberScale}
              maxLength={200}
              placeholder="예) 상근 12명"
              onChange={(e) => setMemberScale(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="org-intro">기업 소개</label>
            <textarea
              id="org-intro"
              rows={6}
              value={intro}
              maxLength={4000}
              onChange={(e) => setIntro(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="org-mission">미션</label>
            <textarea
              id="org-mission"
              rows={3}
              value={mission}
              maxLength={4000}
              onChange={(e) => setMission(e.target.value)}
            />
            <div className="hint">기업 신청 구글폼의 &apos;기업 미션&apos; 문항이 여기로 들어옵니다.</div>
          </div>

          <div className="field">
            <label htmlFor="org-vision">비전</label>
            <textarea
              id="org-vision"
              rows={3}
              value={vision}
              maxLength={4000}
              onChange={(e) => setVision(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="org-homepage">홈페이지</label>
            <input
              id="org-homepage"
              type="url"
              value={homepageUrl}
              placeholder="https://"
              onChange={(e) => setHomepageUrl(e.target.value)}
            />
            <div className="hint">http:// 또는 https:// 로 시작해야 합니다.</div>
          </div>

          <fieldset className="field">
            <legend>사업자등록번호·고유번호 확인</legend>
            {!isNew && org.data !== undefined && (
              <p className="hint" data-testid="biz-reg-status">
                현재 상태:{' '}
                <b>
                  {org.data.bizRegStatus === 'valid'
                    ? '확인됨'
                    : org.data.bizRegStatus === 'pending'
                      ? '확인 대기'
                      : org.data.bizRegStatus === 'not_applicable'
                        ? '고유번호 — 국세청 확인 대상 아님'
                        : '번호 없음'}
                </b>
                {org.data.bizRegCheckedAt !== null &&
                  ` (${new Date(org.data.bizRegCheckedAt).toLocaleDateString('ko-KR')})`}
                .{' '}
                {mayReadContact
                  ? '저장된 번호가 아래에 표시됩니다 — 고치면 다시 확인 후 교체되고, 비운 채 저장하면 삭제됩니다.'
                  : '번호 원문은 pii.read 권한이 있는 계정만 봅니다 — 빈 칸은 값이 없다는 뜻이 아닙니다.'}
              </p>
            )}

            {/*
              종류는 번호만으로 갈리지 않는다 — 고유번호증 번호도 같은 10자리다(021).
              신청서(구글폼)는 한 칸으로 받으므로, 어느 쪽인지는 증빙을 보고 여기서 고른다.
            */}
            <span className="hint">번호 종류</span>
            <div role="radiogroup" aria-label="번호 종류">
              <label style={{ marginRight: 16 }}>
                <input
                  type="radio"
                  name="org-biz-reg-kind"
                  checked={bizRegKind === 'biz_reg'}
                  onChange={() => {
                    setBizRegKind('biz_reg')
                    setBizRegNoDirty(true)
                  }}
                />{' '}
                사업자등록번호
              </label>
              <label>
                <input
                  type="radio"
                  name="org-biz-reg-kind"
                  checked={bizRegKind === 'unique_no'}
                  onChange={() => {
                    setBizRegKind('unique_no')
                    setBizRegNoDirty(true)
                  }}
                />{' '}
                고유번호 (고유번호증)
              </label>
            </div>

            <label htmlFor="org-biz-reg-no">
              {bizRegKind === 'biz_reg' ? '사업자등록번호' : '고유번호'}
            </label>
            <input
              id="org-biz-reg-no"
              type="text"
              inputMode="numeric"
              value={bizRegNo}
              maxLength={12}
              placeholder="000-00-00000"
              onChange={(e) => {
                setBizRegNo(e.target.value)
                setBizRegNoDirty(true)
              }}
            />
            {bizRegKind === 'biz_reg' ? (
              <div className="hint">
                없어도 등록됩니다 — <b>확인 대기</b>로 등록되고 확인 후 공개됩니다. 입력하면
                국세청에 진위를 확인하며, 폐업·휴업이거나 아래 두 항목과 일치하지 않으면
                저장되지 않습니다. 갓 등록한 사업자는 국세청 반영(1~2일) 전이라 확인 대기로
                등록될 수 있습니다.
              </div>
            ) : (
              <div className="hint">
                고유번호는 국세청 사업자 확인을 <b>거치지 않습니다</b> — 화면에{' '}
                <b>확인 대상 아님</b>으로 표시되며, 필요하면 고유번호증 사본으로 직접
                확인합니다. 같은 번호의 중복 등록은 막습니다.
              </div>
            )}

            {bizRegKind === 'biz_reg' && (
              <>
                <label htmlFor="org-ceo-name">
                  대표자 성명 {bizRegNo.trim() !== '' && <span className="req">*</span>}
                </label>
                <input
                  id="org-ceo-name"
                  type="text"
                  value={ceoName}
                  maxLength={60}
                  onChange={(e) => setCeoName(e.target.value)}
                />

                <label htmlFor="org-open-date">
                  개업일자 {bizRegNo.trim() !== '' && <span className="req">*</span>}
                </label>
                <input
                  id="org-open-date"
                  type="date"
                  value={openDate}
                  onChange={(e) => setOpenDate(e.target.value)}
                />
                <div className="hint">
                  두 항목은 국세청 확인에만 쓰고 <b>저장하지 않습니다</b>(사업자등록증 기재
                  기준).
                </div>
              </>
            )}
          </fieldset>

          {mayReadContact ? (
            <fieldset className="field">
              <legend>담당자 (비공개)</legend>
              <p className="hint">
                공개 화면·공개 API에 <b>절대 나가지 않습니다</b>. 연결 요청이 왔을 때 운영자가
                보는 정보입니다.
              </p>

              <label htmlFor="org-contact-name">
                담당자명 <span className="req">*</span>
              </label>
              <input
                id="org-contact-name"
                type="text"
                value={contactName}
                maxLength={60}
                onChange={(e) => setContactName(e.target.value)}
              />

              <label htmlFor="org-contact-phone">
                연락처 <span className="req">*</span>
              </label>
              <input
                id="org-contact-phone"
                type="tel"
                value={contactPhone}
                maxLength={40}
                onChange={(e) => setContactPhone(e.target.value)}
              />

              <label htmlFor="org-contact-email">이메일</label>
              <input
                id="org-contact-email"
                type="email"
                value={contactEmail}
                maxLength={200}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </fieldset>
          ) : (
            !isNew && (
              // 권한이 둘로 갈린다 — 연락처를 못 보는 것(`pii.read`)과 아무것도 못 고치는 것
              // (`org.write`)은 다른 사실이다. 열람자에게 "다른 항목은 수정할 수 있다"고 하면
              // 저장 버튼이 꺼져 있는 이유를 화면에서 찾을 수 없다.
              <div className="notice">
                담당자 연락처는 <b>pii.read</b> 권한이 있는 계정만 보고 고칠 수 있습니다.{' '}
                {mayWrite
                  ? '다른 항목은 그대로 수정할 수 있습니다.'
                  : '이 계정은 열람 전용이라 다른 항목도 저장할 수 없습니다.'}
              </div>
            )
          )}

          <button className="btn" type="submit" disabled={!canSave || save.isPending}>
            {save.isPending ? '저장 중…' : '저장'}
          </button>
        </form>
      </QueryState>

      {!isNew && org.data !== undefined && (
        <section className="panel" style={{ marginTop: 20 }}>
          <div className="admin-bar">
            <h2 className="section-title" style={{ marginTop: 0 }}>
              해결카드 {org.data.solutionCards.length}건
            </h2>
            {mayWrite && (
              <Link className="btn ghost" to={`/admin/cards/new?orgId=${org.data.id}`}>
                + 해결카드 추가
              </Link>
            )}
          </div>
          {org.data.solutionCards.length === 0 ? (
            <p className="muted small">
              아직 해결카드가 없습니다. 매칭은 기업이 아니라 <b>해결카드 단위</b>로 이뤄지므로,
              카드가 없으면 이 기업은 매칭에 나오지 않습니다.
            </p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>제목</th>
                  <th>요약</th>
                  <th>검수 상태</th>
                </tr>
              </thead>
              <tbody>
                {org.data.solutionCards.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {mayWrite ? <Link to={`/admin/cards/${c.id}`}>{c.title}</Link> : c.title}
                    </td>
                    <td className="muted small">{c.summary}</td>
                    <td className="muted small">{c.reviewStatusLabel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  )
}
